import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, KronosSubmission } from '@/lib/supabase';

const SESSION_KEY = 'kronos_su_session';
const SESSION_TTL = 8 * 60 * 60 * 1000; // 8 horas en ms

const SuperuserPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submissions, setSubmissions] = useState<KronosSubmission[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await supabase
      .from('kronos_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (!fetchError && data) {
      setSubmissions(data as KronosSubmission[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      try {
        const session = JSON.parse(raw) as { expires: number };
        if (session.expires > Date.now()) {
          setIsAuthenticated(true);
          loadSubmissions();
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }
  }, [loadSubmissions]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor ingresa tu correo y contraseña');
      return;
    }

    setLoginLoading(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('verify_superuser', {
        p_email: email,
        p_password: password,
      });

      if (rpcError || !data) {
        setError('Credenciales incorrectas. Intenta de nuevo.');
      } else {
        const session = { expires: Date.now() + SESSION_TTL };
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
        setIsAuthenticated(true);
        loadSubmissions();
      }
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setEmail('');
    setPassword('');
    setSubmissions([]);
  };

  const deleteSubmission = async (id: string) => {
    const { error: deleteError } = await supabase
      .from('kronos_submissions')
      .delete()
      .eq('id', id);

    if (!deleteError) {
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
    }
    setDeleteConfirm(null);
  };

  const exportToCSV = () => {
    if (submissions.length === 0) return;
    const headers = ['Folio', 'Fecha', 'Nombre', 'Email', 'Teléfono', 'Servicio', 'Mensaje'];
    const rows = submissions.map((s) => [
      `"${s.folio ?? ''}"`,
      s.created_at,
      `"${s.nombre}"`,
      `"${s.email}"`,
      `"${s.telefono}"`,
      `"${s.servicio}"`,
      `"${s.mensaje.replace(/"/g, '""')}"`,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `kronos_formularios_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const thisMonthCount = submissions.filter((s) => {
    const date = new Date(s.created_at);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1E3A8A] via-[#1a3278] to-[#152a6e] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md">
          {/* Logo / Branding */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1E3A8A] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-keyhole-line text-3xl text-white"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-[#1E3A8A] mb-1">
              Panel de Administración
            </h1>
            <p className="font-body text-sm text-gray-500">
              Kronos Consultoría Contable
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="font-body text-sm font-semibold text-gray-700 block mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg font-body text-sm focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all"
                  placeholder="correo@ejemplo.com"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="font-body text-sm font-semibold text-gray-700 block mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg font-body text-sm focus:ring-2 focus:ring-[#1E3A8A] focus:border-[#1E3A8A] outline-none transition-all"
                  placeholder="••••••••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'}></i>
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-center space-x-2">
                <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                <span className="font-body text-sm text-red-600">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-[#1E3A8A] hover:bg-[#1a3278] text-white font-heading font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loginLoading ? (
                <span className="flex items-center justify-center space-x-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Verificando...</span>
                </span>
              ) : (
                'Ingresar al panel'
              )}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="w-full mt-5 text-gray-400 hover:text-[#1E3A8A] font-body text-sm transition-colors cursor-pointer"
          >
            ← Volver al sitio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-lg"></i>
              </div>
              <div>
                <h1 className="font-heading font-bold text-base">Panel de Administración</h1>
                <p className="font-body text-xs text-white/60">Kronos Consultoría Contable — Supabase</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-sm font-body text-white/70 hover:text-[#D4AF37] transition-colors cursor-pointer whitespace-nowrap"
              >
                Ver sitio
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg font-body text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line"></i>
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500 mb-1">Total de solicitudes</p>
                <p className="font-heading font-bold text-3xl text-[#1E3A8A]">{submissions.length}</p>
              </div>
              <div className="w-12 h-12 bg-[#1E3A8A]/10 rounded-xl flex items-center justify-center">
                <i className="ri-file-list-3-line text-2xl text-[#1E3A8A]"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500 mb-1">Este mes</p>
                <p className="font-heading font-bold text-3xl text-[#1E3A8A]">{thisMonthCount}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <i className="ri-calendar-check-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-body text-sm text-gray-500 mb-1">Último registro</p>
                <p className="font-heading font-bold text-lg text-[#1E3A8A]">
                  {submissions.length > 0
                    ? new Date(submissions[0].created_at).toLocaleDateString('es-MX', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '—'}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-2xl text-[#D4AF37]"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-5">
          <h2 className="font-heading font-bold text-xl text-gray-800">Solicitudes de información</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={loadSubmissions}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 hover:border-gray-300 rounded-lg font-body text-sm transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
              <span>{loading ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
            {submissions.length > 0 && (
              <button
                onClick={exportToCSV}
                className="flex items-center space-x-2 px-4 py-2 bg-[#1E3A8A] hover:bg-[#1a3278] text-white rounded-lg font-body text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line"></i>
                <span>Exportar CSV</span>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <div className="w-10 h-10 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="font-body text-sm text-gray-400">Cargando desde Supabase...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="p-16 text-center">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-4xl text-gray-300"></i>
              </div>
              <h3 className="font-heading font-semibold text-lg text-gray-600 mb-2">Sin registros aún</h3>
              <p className="font-body text-sm text-gray-400">
                Aquí aparecerán las solicitudes cuando los usuarios envíen el formulario
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Folio', 'Fecha', 'Nombre', 'Contacto', 'Servicio', 'Mensaje', ''].map((col, i) => (
                      <th
                        key={i}
                        className="px-6 py-4 text-left font-heading font-semibold text-xs text-gray-500 uppercase tracking-wider"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {submissions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-xs font-semibold text-[#1E3A8A] bg-[#1E3A8A]/8 px-2 py-1 rounded tracking-wider">
                          {sub.folio ?? '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="font-body text-sm text-gray-700">
                          {new Date(sub.created_at).toLocaleDateString('es-MX', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </p>
                        <p className="font-body text-xs text-gray-400">
                          {new Date(sub.created_at).toLocaleTimeString('es-MX', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-body text-sm font-semibold text-gray-800">{sub.nombre}</p>
                      </td>
                      <td className="px-6 py-4 min-w-[180px]">
                        <a
                          href={`mailto:${sub.email}`}
                          className="flex items-center space-x-1.5 font-body text-sm text-[#1E3A8A] hover:underline mb-1"
                        >
                          <i className="ri-mail-line text-xs flex-shrink-0"></i>
                          <span className="truncate max-w-[160px]">{sub.email}</span>
                        </a>
                        <a
                          href={`tel:${sub.telefono}`}
                          className="flex items-center space-x-1.5 font-body text-sm text-gray-500 hover:text-[#1E3A8A]"
                        >
                          <i className="ri-phone-line text-xs flex-shrink-0"></i>
                          <span>{sub.telefono}</span>
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full font-body text-xs font-medium bg-[#D4AF37]/15 text-[#1E3A8A]">
                          {sub.servicio}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <p
                          className="font-body text-sm text-gray-600 truncate"
                          title={sub.mensaje}
                        >
                          {sub.mensaje}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {deleteConfirm === sub.id ? (
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => deleteSubmission(sub.id)}
                              className="text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg font-body transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Confirmar
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1 rounded-lg font-body transition-colors cursor-pointer"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(sub.id)}
                            className="text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line text-lg"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SuperuserPage;
